import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { BookOpen, CheckCircle, Clock, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { applyPageMeta, SITE_URL } from '../components/SEOMetaTags';

/**
 * Pagină ghid (articol informațional)
 * URL: /ghid/:slug/  ·  Conținut: /seo-data/ghid/{slug}.md  ·  Metadate: /seo-data/ghid/index.json
 * Pre-randată la build (scripts/prerender.js) → textul ajunge direct în HTML.
 */

const formatDateRo = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
};

function GuidePage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [related, setRelated] = useState([]);
  const [content, setContent] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const idx = await (await fetch('/seo-data/ghid/index.json')).json();
        const g = (idx.guides || []).find(x => x.slug === slug);
        if (!g) { setNotFound(true); return; }
        const res = await fetch(`/seo-data/ghid/${slug}.md`);
        if (!res.ok) throw new Error('Conținut lipsă');
        const md = await res.text();
        setGuide(g);
        setRelated((idx.guides || []).filter(x => x.slug !== slug));
        setContent(md.replace(/^---[\s\S]*?---\n/, ''));
      } catch (e) {
        console.error('Eroare încărcare ghid:', e);
        setNotFound(true);
      }
    };
    load();
  }, [slug]);

  // Meta + schema Article (setate sincron după încărcare → prinse de prerender)
  useEffect(() => {
    if (!guide) return;
    const url = `${SITE_URL}/ghid/${guide.slug}/`;
    applyPageMeta({ title: guide.title, description: guide.description, canonical: url });

    let script = document.querySelector('script[data-schema="article"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'article');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: guide.h1,
      description: guide.description,
      datePublished: guide.datePublished,
      dateModified: guide.dateModified,
      inLanguage: 'ro-RO',
      mainEntityOfPage: url,
      author: { '@type': 'Organization', name: 'CF-Extras.ro', url: `${SITE_URL}/` },
      publisher: {
        '@type': 'Organization',
        name: 'CF-Extras.ro',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo-cf.png` }
      }
    });

    let crumbs = document.querySelector('script[data-schema="guide-breadcrumb"]');
    if (!crumbs) {
      crumbs = document.createElement('script');
      crumbs.type = 'application/ld+json';
      crumbs.setAttribute('data-schema', 'guide-breadcrumb');
      document.head.appendChild(crumbs);
    }
    crumbs.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Acasă', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Ghiduri', item: `${SITE_URL}/ghid/` },
        { '@type': 'ListItem', position: 3, name: guide.h1, item: url }
      ]
    });

    return () => {
      document.querySelector('script[data-schema="article"]')?.remove();
      document.querySelector('script[data-schema="guide-breadcrumb"]')?.remove();
    };
  }, [guide]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Ghidul nu a fost găsit</h1>
          <Link to="/ghid/" className="text-emerald-700 hover:underline">Vezi toate ghidurile</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <nav className="text-sm mb-4">
            <ol className="flex items-center flex-wrap gap-x-2 text-gray-600">
              <li><Link to="/" className="hover:text-emerald-600">Acasă</Link></li>
              <li>/</li>
              <li><Link to="/ghid/" className="hover:text-emerald-600">Ghiduri</Link></li>
              <li>/</li>
              <li className="text-gray-900 font-medium">{guide.short}</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{guide.h1}</h1>
          <p className="text-sm text-gray-500 flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" aria-hidden="true" /> {guide.readMinutes} minute de citit
            </span>
            <span>Actualizat: {formatDateRo(guide.dateModified)}</span>
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 max-w-3xl">
        <article className="bg-white rounded-2xl shadow-lg p-6 sm:p-10 text-gray-700 leading-relaxed">
          <div className="guide-content">
            <ReactMarkdown
              components={{
                h2: ({ node, children, ...props }) => <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" {...props}>{children}</h2>,
                h3: ({ node, children, ...props }) => <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2" {...props}>{children}</h3>,
                p: ({ node, ...props }) => <p className="mb-4" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-2 mb-4" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-2 mb-4" {...props} />,
                strong: ({ node, ...props }) => <strong className="text-gray-900" {...props} />,
                blockquote: ({ node, ...props }) => (
                  <blockquote className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 my-6 text-amber-900" {...props} />
                ),
                a: ({ node, href, children, ...props }) =>
                  href && href.startsWith('/')
                    ? <Link to={href} className="text-emerald-700 hover:underline">{children}</Link>
                    : <a href={href} className="text-emerald-700 hover:underline" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
              }}
            >
              {content}
            </ReactMarkdown>
          </div>

          {/* CTA */}
          <div className="mt-10 p-6 bg-emerald-50 rounded-xl border border-emerald-200">
            <p className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-600" aria-hidden="true" />
              Ai nevoie de un extras de carte funciară?
            </p>
            <p className="mb-4">
              Comanzi online pentru orice imobil din România și primești documentul oficial pe email în aproximativ 5 minute.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition"
            >
              <FileText className="w-5 h-5" aria-hidden="true" />
              Comandă extrasul acum
            </Link>
          </div>
        </article>

        {/* Ghiduri înrudite */}
        {related.length > 0 && (
          <aside className="mt-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" aria-hidden="true" />
              Alte ghiduri utile
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {related.map(r => (
                <li key={r.slug}>
                  <Link
                    to={`/ghid/${r.slug}/`}
                    className="block h-full p-4 bg-white rounded-xl shadow hover:shadow-md transition text-emerald-800 font-medium"
                  >
                    {r.h1}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default GuidePage;
